import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material/Autocomplete';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import React, { memo, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { ClassNames } from '@emotion/react';

type MakeOptional<T, K extends keyof T> = {
  [P in K]?: T[P] | undefined;
} & Omit<T, K>;

export type AutocompleteProps<Option> = {
  name?: string;
  label?: string;
  testId?: string;
  options?: Option[]
  defaultValue?: Option;
  onInputChange?: (event: SyntheticEvent | null, value: string) => void | Promise<void>
  onBlur?: () => void;
  textFieldProps?: TextFieldProps;
} & MakeOptional<MuiAutocompleteProps<Option, false, false, false>, 'renderInput'>

type FinalOption<T> = AutocompleteOption & T

export type AutocompleteOption = {
  id: number | string | null;
  name: string;
}

function defaultOnInputChange() {
}

function defaultGetOptionLabel<Option>(option: FinalOption<Option>) {
  return option.name;
}

function AutocompleteField<Option>({
  options = [],
  onChange,
  loading,
  textFieldProps,
  onInputChange = defaultOnInputChange,
  getOptionLabel = defaultGetOptionLabel,
  value,
  ...props
}: AutocompleteProps<FinalOption<Option>>) {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setInputValue(value?.name || '');
    onInputChange(null, value?.name || '');
  }, [onInputChange, value?.name]);

  const handleChange = useCallback(
    (
      event: SyntheticEvent,
      option: FinalOption<Option> | null,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<FinalOption<Option>>,
    ) => {
      onChange?.(event, option, reason, details);
    },
    [onChange],
  );

  const handleInputChange = useCallback((event: SyntheticEvent, value: string, reason: string) => {
    if (reason === 'clear') {
      handleChange(event, null, reason);
    }

    if (event) {
      setInputValue(value);
      onInputChange(event, value);
    }
  }, [handleChange, onInputChange]);

  return (
    <ClassNames>
      {({ css }) => (
        <Autocomplete
          {...props}
          data-testid={props.testId}
          options={options}
          getOptionLabel={getOptionLabel}
          onChange={handleChange}
          onInputChange={handleInputChange}
          value={value}
          inputValue={inputValue}
          classes={{
            noOptions: css({
              padding: 0,
            }),
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...textFieldProps}
              inputProps={{
                ...params.inputProps,
                ...textFieldProps?.inputProps,
              }}
              InputProps={{
                ...params.InputProps,
                ...textFieldProps?.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20}/> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    </ClassNames>
  );
}

export default memo(AutocompleteField);
